import { ChannelIdZod } from '@onr/schema'
import { collection, type FirestoreDataConverter, getFirestore, Timestamp } from 'firebase/firestore'
import { z } from 'zod'

import { ErrorWithSentryCaptureContext } from '@/sentry/ErrorWithSentryCaptureContext.ts'

export const BaseDashboardAlertZod = z.object({
  timestamp: z.instanceof(Timestamp).transform((v) => v.toDate()),
  regionId: z.string(),
  eveningId: z.number(),
})

export const TransportRequestDashboardAlertEventZod = z.enum(['customerCancel', 'teamCancel', 'teamUpdateDestination'])
export type TransportRequestDashboardAlertEvent = z.infer<typeof TransportRequestDashboardAlertEventZod>

export const TransportRequestDashboardAlertZod = BaseDashboardAlertZod.extend({
  type: z.literal('transportRequest'),
  transportRequestId: z.string(),
  transportRequestPiloteId: z.number(),
  event: TransportRequestDashboardAlertEventZod,
})
export type TransportRequestDashboardAlert = z.infer<typeof TransportRequestDashboardAlertZod>

export const TeamMessageDashboardAlertZod = BaseDashboardAlertZod.extend({
  type: z.literal('teamMessage'),
  teamId: z.string(),
  teamNumber: z.number(),
  hasText: z.boolean().optional(),
  hasPicture: z.boolean().optional(),
  hasAudio: z.boolean().optional(),
})
export type TeamMessageDashboardAlert = z.infer<typeof TeamMessageDashboardAlertZod>

export const ChannelMessageDashboardAlertZod = BaseDashboardAlertZod.extend({
  type: z.literal('channelMessage'),
  channelId: ChannelIdZod,
  messageId: z.string(),
  sender: z.enum(['volunteer', 'admin']),
  senderId: z.string(),
  senderName: z.string(),
  teamNumber: z.number().optional(),
  textExcerpt: z.string(),
})
export type ChannelMessageDashboardAlert = z.infer<typeof ChannelMessageDashboardAlertZod>

export const DashboardAlertZod = z.discriminatedUnion('type', [
  TransportRequestDashboardAlertZod,
  TeamMessageDashboardAlertZod,
  ChannelMessageDashboardAlertZod,
])
export type DashboardAlert = z.infer<typeof DashboardAlertZod> & { _id: string }

const DashboardAlertConverter: FirestoreDataConverter<DashboardAlert> = {
  toFirestore: (data) => data,
  fromFirestore: (snapshot, options) => {
    const result = DashboardAlertZod.safeParse(snapshot.data(options))
    if (!result.success) {
      throw new ErrorWithSentryCaptureContext(
        `Invalid transportRequest ${snapshot.id}`,
        {
          tags: { 'firestore.zod': 'transportRequests' },
          fingerprint: ['firestore', 'zod', 'transportRequests'],
          extra: { data: snapshot.data(options), document: { path: snapshot.ref.path } },
        },
        { cause: result.error }
      )
    }
    return { _id: snapshot.id, ...result.data }
  },
}

export const dashboardAlertCollection = () =>
  collection(getFirestore(), 'dashboardAlerts').withConverter(DashboardAlertConverter)
